// The three doors and the one protected endpoint, as the browser sees them.
//
// Nothing here stores a token. The access token is returned to the caller and
// held in memory by the session provider; the refresh token is in an httpOnly
// cookie this code cannot read and does not try to. That division is the whole
// reason the portal does not keep a credential in localStorage, where any
// cross-site scripting flaw on any page of this origin could read it.
//
// Requests are same-origin. In production the SPA and the API share a host; in
// development vite proxies /auth and /portal/api to the backend (see
// vite.config.ts), so the cookie the backend sets is a first-party cookie
// either way.

/** Where the session doors live. The refresh cookie is scoped to this prefix. */
const AUTH_BASE = '/auth';

/**
 * Where the portal's bearer-token endpoints live.
 *
 * Exported so that a screen's data module names the prefix once rather than
 * repeating the literal: the refresh cookie is deliberately NOT scoped to this
 * path, and a typo'd prefix would be a request that quietly carried no
 * credential at all.
 */
export const API_BASE = '/portal/api';

/**
 * A request that bears the session's access token, renewing it once if the
 * service says it has expired.
 *
 * The signature of `authorizedFetch` on the session context. Named here so a
 * data module can take one as an argument and stay ignorant of where tokens
 * live — which is the property that keeps the token in the provider's memory
 * and out of every screen.
 */
export type AuthorizedFetch = (path: string, init?: RequestInit) => Promise<Response>;

/**
 * A customer, as the portal ever names one: its public identifier and its name.
 *
 * The same shape whether it is the customer this session acts for or one it may
 * switch into, because it is the same thing said about a different row — and a
 * second shape for the second use would be a second thing to keep in step with
 * the wire.
 */
export interface NamedCustomer {
  id: string;
  name: string;
}

/**
 * Where else this session's person may act. On both shapes of identity.
 *
 * `may_act_for` is every customer they hold a verified membership on, read off
 * the membership table by the backend and never off the token — a token names
 * one customer and could not answer this question even if it were trusted to.
 * `may_act_across_all_customers` says whether the fleet-wide view is open to
 * them.
 *
 * On BOTH shapes, deliberately. The discrimination between the two identities
 * stays exactly where it was — `customer` present, or `role` present — so
 * `actsForOneCustomer` below is untouched by this widening and `customer` did
 * not become optional. A field that appeared on only one shape would be a
 * second discriminant, and two ways to ask the same question is how they come
 * to disagree.
 */
interface MayAlsoActFor {
  may_act_for: NamedCustomer[];
  may_act_across_all_customers: boolean;
}

/**
 * Somebody acting for one customer, and which one.
 *
 * The shape GET /portal/api/me/ answers a customer's token in. Kept as the
 * wire's own field names, for the reason data/reads.ts gives.
 */
export interface CustomerIdentity extends MayAlsoActFor {
  user: { email: string };
  customer: NamedCustomer;
}

/**
 * A metricsIQ person acting across every customer, and for none of them.
 *
 * **There is no `customer` key here, and its absence is the design** — the
 * same absence `StaffActor` carries on the backend. An optional
 * `customer?: …` would have type-checked at every site that reads
 * `identity.customer`, and every one of those sites would then have had to
 * remember what an absent customer means. Absent from the type, the compiler
 * names them all instead.
 *
 * `role` is the grant this session was opened under, re-read from the grants
 * themselves on every request — see backend portal_auth/models.py FleetRole.
 * It arrives as the codename (`see_all_customers`); what a codename should
 * READ as is the screen's business, so the wording lives in AppShell beside
 * the nav it labels.
 */
export interface StaffIdentity extends MayAlsoActFor {
  user: { email: string };
  role: string;
}

/**
 * Who the backend says is signed in — the shape of GET /portal/api/me/.
 *
 * A sum rather than one shape with a nullable customer, because these are
 * genuinely two different people to this app: one is owed their own account's
 * screens, the other is owed the fleet-wide ones and is refused the first set
 * at the door — 401 from every customer endpoint, 404 from every staff one.
 * Which shape arrived is itself the answer.
 */
export type PortalIdentity = CustomerIdentity | StaffIdentity;

/**
 * Which of the two shapes this is, asked once so it is asked the same way.
 *
 * Discriminated on the presence of `customer` rather than on a tag field,
 * because the wire has no tag: `whoami` answers a `customer` block or a
 * `role` and never both. A discriminant invented on this side would be a fact
 * about this app rather than about the response — one more thing to keep in
 * step the next time the shape moves.
 */
export function actsForOneCustomer(
  identity: PortalIdentity,
): identity is CustomerIdentity {
  return 'customer' in identity;
}

/**
 * Somewhere a session could move to: a named customer, or the fleet-wide view.
 *
 * A sum for the same reason the identity is one, and shaped like it — a
 * `customer` key or not. What it is NOT is the request body: a switch names a
 * customer by its identifier alone, and this carries the name too, because a
 * switcher that offered identifiers would be a switcher nobody could read.
 * `scopeOf` below is the translation, so the shape shown and the shape sent
 * are converted in one place rather than assembled twice.
 */
export type PlaceToAct = { customer: NamedCustomer } | { acrossAllCustomers: true };

/**
 * Everywhere this identity may act EXCEPT where it already is.
 *
 * The switcher's list, and the subtraction is the whole of it: `whoami`
 * reports what somebody may act for, which includes the customer they are in,
 * and an affordance offering the place you already are is an affordance that
 * does nothing when pressed. Empty means there is exactly one place to be,
 * which is what decides whether the control is drawn at all.
 *
 * Asked here rather than in the shell because this is where the wire's shape
 * is known — the current customer is matched by identifier, and identifiers do
 * not reach the shell. The shell asks "is there anywhere to go", which is a
 * question about a list.
 */
export function placesToActBesidesHere(identity: PortalIdentity): PlaceToAct[] {
  const here = actsForOneCustomer(identity) ? identity.customer.id : null;
  const elsewhere: PlaceToAct[] = identity.may_act_for
    .filter((customer) => customer.id !== here)
    .map((customer) => ({ customer }));

  // The fleet-wide view is only somewhere to GO for somebody who is not
  // already there — which, since a staff identity is exactly somebody who is,
  // means a customer identity that also holds the role.
  return actsForOneCustomer(identity) && identity.may_act_across_all_customers
    ? [...elsewhere, { acrossAllCustomers: true }]
    : elsewhere;
}

/** The scope to ask `/auth/switch/` for, to reach a place. */
export function scopeOf(place: PlaceToAct): ActingScope {
  return 'customer' in place ? { customer: place.customer.id } : place;
}

/** A token and the seconds it is good for. */
export interface GrantedAccess {
  accessToken: string;
  expiresInSeconds: number;
}

/** The service refused, and this is what it said. */
export class PortalRequestFailed extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'PortalRequestFailed';
    this.status = status;
  }
}

/**
 * Exchange an email and password for an access token and a refresh cookie.
 *
 * `keepSignedIn` decides whether the refresh cookie outlives the browser
 * window: the backend gives it an expiry when true and makes it a session
 * cookie when false. Either way the token itself expires on the server's
 * schedule — the checkbox shortens the sign-in, it cannot lengthen it.
 *
 * @throws PortalRequestFailed if the credentials are not accepted.
 */
export async function requestSignIn(
  email: string,
  password: string,
  keepSignedIn: boolean,
): Promise<GrantedAccess> {
  const response = await fetch(`${AUTH_BASE}/token/`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password, keep_signed_in: keepSignedIn }),
  });

  return grantFrom(response);
}

/**
 * Trade the refresh cookie for a fresh access token, rotating the cookie.
 *
 * Called once on boot to decide signed-in from signed-out, and again whenever a
 * request comes back 401 because the access token aged out mid-session.
 *
 * @throws PortalRequestFailed if there is no usable cookie. On boot that is not
 *   an error condition — it is what "signed out" looks like — so the caller
 *   distinguishes the two, not this function.
 */
export async function requestRefresh(): Promise<GrantedAccess> {
  const response = await fetch(`${AUTH_BASE}/refresh/`, {
    method: 'POST',
    credentials: 'same-origin',
  });

  return grantFrom(response);
}

/**
 * Where a switch is going: one customer, or the fleet-wide view.
 *
 * A sum discriminated on the presence of `customer`, which is the same way
 * `actsForOneCustomer` reads an identity and the same way the backend's own
 * request body is written — one idiom for "which of the two scopes is this",
 * asked on the way out and on the way back.
 */
export type ActingScope = { customer: string } | { acrossAllCustomers: true };

/**
 * Move this session into another scope its person is entitled to.
 *
 * A sign-out and a sign-in in one act: the backend revokes the family behind
 * the current scope and opens one for the chosen scope, so what comes back is
 * an ordinary grant — a new access token, and the refresh cookie rotated onto
 * the new sign-in. The old token is dead, not merely superseded.
 *
 * Entitlement is the backend's answer and only the backend's: it checks the
 * membership table for a named customer and the grants for the fleet view.
 * This function does not filter, and the list it is called from is the same
 * list `whoami` reported — but a scope somebody is not entitled to is refused
 * there, not here.
 *
 * @throws PortalRequestFailed if the session cannot switch, or cannot switch
 *   into what it named. Both are 401 in the service's one refusal wording,
 *   which the screen shows as-is rather than guessing which it was.
 */
export async function requestScopeSwitch(scope: ActingScope): Promise<GrantedAccess> {
  const response = await fetch(`${AUTH_BASE}/switch/`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(
      'customer' in scope ? { customer: scope.customer } : { across_all_customers: true },
    ),
  });

  return grantFrom(response);
}

/**
 * Revoke the refresh family and clear the cookie.
 *
 * Answers 200 whatever it finds, so there is nothing to check: a sign-out that
 * could report whether the cookie was still good would answer that question for
 * anybody holding a stolen one.
 */
export async function requestSignOut(): Promise<void> {
  await fetch(`${AUTH_BASE}/logout/`, {
    method: 'POST',
    credentials: 'same-origin',
  });
}

/**
 * Ask the backend who the bearer token belongs to.
 *
 * @throws PortalRequestFailed if the token is not accepted.
 */
export async function requestIdentity(accessToken: string): Promise<PortalIdentity> {
  const response = await fetch(`${API_BASE}/me/`, {
    method: 'GET',
    credentials: 'same-origin',
    headers: { Authorization: `Bearer ${accessToken}` },
  });

  return jsonOrRefusal<PortalIdentity>(response);
}

/**
 * The body of a portal API response, or the service's own refusal as an error.
 *
 * Every one of the portal's JSON endpoints answers in the same two shapes — a
 * body on success, `{"error": ...}` on refusal — so unwrapping one is the same
 * work every time. Shared rather than repeated so a screen cannot accidentally
 * treat a 401 as data.
 *
 * @throws PortalRequestFailed with the service's wording, which a screen shows
 *   as-is rather than substituting a message of its own.
 */
export async function jsonOrRefusal<T>(response: Response): Promise<T> {
  if (!response.ok) {
    throw new PortalRequestFailed(response.status, await refusalFrom(response));
  }
  return (await response.json()) as T;
}

async function grantFrom(response: Response): Promise<GrantedAccess> {
  if (!response.ok) {
    throw new PortalRequestFailed(response.status, await refusalFrom(response));
  }

  const body = (await response.json()) as { access_token: string; expires_in: number };
  return { accessToken: body.access_token, expiresInSeconds: body.expires_in };
}

/**
 * What the service said, sentence-cased, or a stand-in if it said nothing.
 *
 * Two keys, because the service has two refusal vocabularies: the identity
 * and acting doors answer `error` (commerce/json_requests.py), and the period
 * and range reads answer `detail` — the sentence a 400 carries so a caller can
 * correct the month or the days it asked for. Reading only the first threw
 * the second away, and a screen told "The service answered 400" cannot say
 * which date was wrong.
 */
async function refusalFrom(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { error?: string; detail?: string };
    const said = body.error ?? body.detail;
    if (said) {
      return said.charAt(0).toUpperCase() + said.slice(1);
    }
  } catch {
    // A refusal with no JSON body — a proxy or gateway answering instead of
    // the service. The status is all there is to report.
  }
  return `The service answered ${response.status}`;
}
